import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Brain, Type, Images, Flame, Trophy, Target, Lock, Star, Info, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/common';
import { useStatsStore, useAchievementsStore, type QuizLength } from '@/store';
import type { QuizMode } from '@/types';
import countries from '@/data/countries.json';

const TOTAL_COUNTRIES = countries.length;
const QUIZ_LENGTHS: QuizLength[] = [20, 50, 100, 'all'];

const quizModes: { mode: QuizMode; icon: typeof Brain; title: string; description: string }[] = [
  {
    mode: 'flag-to-name-mc',
    icon: Brain,
    title: 'Find the Country',
    description: 'See a flag, pick the country name',
  },
  {
    mode: 'flag-to-name-type',
    icon: Type,
    title: 'Name the Country',
    description: 'See a flag, type the country name',
  },
  {
    mode: 'name-to-flag-mc',
    icon: Images,
    title: 'Find the Flag',
    description: 'See a country name, pick its flag',
  },
];

function getLengthLabel(length: QuizLength): string {
  return length === 'all' ? `All ${TOTAL_COUNTRIES}` : String(length);
}

export function HomePage() {
  const navigate = useNavigate();
  const { overall } = useStatsStore();
  const { isLengthUnlocked, hasPerfectScore, getAchievements } = useAchievementsStore();
  const [expandedMode, setExpandedMode] = useState<QuizMode | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const accuracy = overall.totalQuestions > 0
    ? Math.round((overall.correctAnswers / overall.totalQuestions) * 100)
    : 0;

  const achievements = getAchievements();

  const startQuiz = (mode: QuizMode, length: QuizLength) => {
    const count = length === 'all' ? TOTAL_COUNTRIES : length;
    navigate(`/quiz?mode=${mode}&count=${count}`);
  };

  const toggleMode = (mode: QuizMode) => {
    setExpandedMode(expandedMode === mode ? null : mode);
  };

  return (
    <div className="space-y-8">
      {/* Hero section */}
      <div className="text-center space-y-3">
        <h1 className="text-3xl md:text-4xl text-stone-900 dark:text-stone-100">
          Master World Flags
        </h1>
        <p className="text-stone-600 dark:text-stone-400 max-w-md mx-auto">
          Learn all {TOTAL_COUNTRIES} country flags with spaced repetition and fun quizzes
        </p>
      </div>

      {/* Quick stats */}
      {overall.totalQuestions > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <Card className="text-center p-4">
            <Flame className="w-5 h-5 mx-auto mb-1.5 text-amber-600" />
            <div className="text-xl font-semibold text-stone-900 dark:text-stone-100">
              {overall.currentStreak}
            </div>
            <div className="text-xs text-stone-500">Day Streak</div>
          </Card>

          <Card className="text-center p-4">
            <Target className="w-5 h-5 mx-auto mb-1.5 text-lime-700" />
            <div className="text-xl font-semibold text-stone-900 dark:text-stone-100">
              {accuracy}%
            </div>
            <div className="text-xs text-stone-500">Accuracy</div>
          </Card>

          <Card className="text-center p-4">
            <Trophy className="w-5 h-5 mx-auto mb-1.5 text-secondary-600" />
            <div className="text-xl font-semibold text-stone-900 dark:text-stone-100">
              {overall.totalQuizzes}
            </div>
            <div className="text-xs text-stone-500">Quizzes</div>
          </Card>
        </div>
      )}

      {/* Achievements display */}
      {achievements.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-amber-500" />
            <h3 className="font-medium text-stone-900 dark:text-stone-100">Achievements</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {achievements.map(({ mode, length }) => {
              const modeInfo = quizModes.find((m) => m.mode === mode);
              return (
                <div
                  key={`${mode}-${length}`}
                  className="px-2.5 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 rounded-full text-xs font-medium flex items-center gap-1"
                >
                  <Star className="w-3 h-3" />
                  {modeInfo?.title} {getLengthLabel(length)}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Quiz mode selection */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg text-stone-900 dark:text-stone-100">Start Training</h2>
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="p-1.5 text-stone-500 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
            aria-label="How it works"
          >
            <Info className="w-5 h-5" />
          </button>
        </div>

        {/* Info modal */}
        {showInfo && (
          <Card className="p-4 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-medium text-primary-900 dark:text-primary-100">How Progression Works</h3>
              <button
                onClick={() => setShowInfo(false)}
                className="text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="text-sm text-primary-800 dark:text-primary-200 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-primary-500 shrink-0" />
                Start with 20-question quizzes
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-primary-500 shrink-0" />
                Get a perfect score to unlock the next level
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-1.5 w-1 h-1 rounded-full bg-primary-500 shrink-0" />
                Progress: 20 → 50 → 100 → All {TOTAL_COUNTRIES}
              </li>
              <li className="flex items-start gap-2">
                <Star className="w-3 h-3 mt-0.5 text-amber-500 shrink-0" />
                Earn achievements for each perfect score!
              </li>
            </ul>
          </Card>
        )}

        <div className="grid gap-3">
          {quizModes.map(({ mode, icon: Icon, title, description }) => {
            const isExpanded = expandedMode === mode;
            return (
              <div key={mode}>
                <Card
                  variant="interactive"
                  onClick={() => toggleMode(mode)}
                  className="flex items-center gap-4"
                >
                  <div className="p-2.5 bg-primary-100 dark:bg-primary-900/30 rounded-md">
                    <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-stone-900 dark:text-stone-100">{title}</h3>
                    <p className="text-sm text-stone-500 dark:text-stone-400">
                      {description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-stone-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-stone-400" />
                    )}
                  </div>
                </Card>

                {/* Quiz length options */}
                {isExpanded && (
                  <div className="mt-2 ml-4 space-y-2">
                    <p className="text-xs text-stone-500">Number of questions</p>
                    <div className="grid grid-cols-2 gap-2">
                    {QUIZ_LENGTHS.map((length) => {
                      const unlocked = isLengthUnlocked(mode, length);
                      const hasPerfect = hasPerfectScore(mode, length);

                      return (
                        <button
                          key={length}
                          onClick={() => unlocked && startQuiz(mode, length)}
                          disabled={!unlocked}
                          className={`
                            relative flex items-center justify-center gap-2 p-3 rounded-lg border transition-all
                            ${unlocked
                              ? 'bg-white dark:bg-stone-800 border-stone-200 dark:border-stone-700 hover:border-primary-400 dark:hover:border-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer'
                              : 'bg-stone-100 dark:bg-stone-900 border-stone-200 dark:border-stone-800 cursor-not-allowed opacity-60'
                            }
                          `}
                        >
                          {!unlocked && (
                            <Lock className="w-4 h-4 text-stone-400" />
                          )}
                          {hasPerfect && (
                            <Star className="w-4 h-4 text-amber-500" />
                          )}
                          <span className={`font-medium ${unlocked ? 'text-stone-900 dark:text-stone-100' : 'text-stone-500'}`}>
                            {getLengthLabel(length)}
                          </span>
                          {unlocked && (
                            <Play className="w-3.5 h-3.5 text-primary-500" />
                          )}
                        </button>
                      );
                    })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-3">
        <Card
          variant="interactive"
          onClick={() => navigate('/learn')}
          className="text-center py-6"
        >
          <div className="text-2xl mb-1">📚</div>
          <div className="font-medium text-stone-900 dark:text-stone-100">Browse Flags</div>
          <div className="text-sm text-stone-500">Learn with tips</div>
        </Card>

        <Card
          variant="interactive"
          onClick={() => navigate('/stats')}
          className="text-center py-6"
        >
          <div className="text-2xl mb-1">📊</div>
          <div className="font-medium text-stone-900 dark:text-stone-100">View Progress</div>
          <div className="text-sm text-stone-500">See your stats</div>
        </Card>
      </div>
    </div>
  );
}
